import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

export type AdvisorClient = {
  id: string;
  company_name: string;
  customer_organization_id: string | null;
  /** What they are invoiced in, decided by where they are. */
  invoiced_in?: string | null;
};

export type Perspective = 'advisor' | 'own';

export type ClientScope = {
  client: AdvisorClient;
  clientId: string;
  orgId: string | null;
  perspective: Perspective;
  basePath: string;
  processPath: (specId: string) => string;
};

const Context = React.createContext<ClientScope | null>(null);

export function useOptionalClientScope() {
  return React.useContext(Context);
}

export function useClientScope() {
  const scope = React.useContext(Context);
  if (!scope) throw new Error('useClientScope must be used inside a client scope');
  return scope;
}

export function RequiresClientOrg({ children }: { children: React.ReactNode }) {
  const { client, orgId } = useClientScope();
  if (!orgId) return <NotOnBitswanYet client={client} />;
  return <>{children}</>;
}

export function useClientOrg() {
  const scope = useClientScope();
  if (!scope.orgId) {
    throw new Error('useClientOrg must be used inside RequiresClientOrg');
  }
  return { ...scope, orgId: scope.orgId };
}

export function useIsOwnCompany() {
  return useClientScope().perspective === 'own';
}

export function useCompanyName() {
  const { client, perspective } = useClientScope();
  return perspective === 'own' ? 'your company' : client.company_name;
}


export function ClientScopeProvider({
  value,
  children,
}: {
  value: ClientScope;
  children: React.ReactNode;
}) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function ScopeLoading() {
  return (
    <div className="flex justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

export function useAdvisorClient(clientId?: string) {
  return useQuery<AdvisorClient>({
    queryKey: ['advisor-client', clientId],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get(`/advisor/clients/${clientId}/`);
      return res.data;
    },
    enabled: Boolean(clientId),
  });
}

export function NotOnBitswanYet({ client }: { client: AdvisorClient }) {
  const queryClient = useQueryClient();

  const setUp = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.post(
        `/advisor/clients/${client.id}/create-organization/`,
      );
      return res.data;
    },
    onSuccess: () => {
      toast.success(`${client.company_name} is on BitSwan.`);
      queryClient.invalidateQueries({ queryKey: ['advisor-client', client.id] });
      queryClient.invalidateQueries({ queryKey: ['advisor-clients'] });
    },
    onError: (error) =>
      toast.error(apiError(error, 'Could not set them up on BitSwan.')),
  });

  return (
    <div
      className="rounded-lg border border-dashed px-6 py-12 text-center"
      data-testid="client-has-no-org"
    >
      <Building2 className="mx-auto h-6 w-6 text-muted-foreground" />
      <p className="mt-3 text-sm font-medium">
        {client.company_name} is not on BitSwan yet
      </p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        Servers live in their own organisation. Set one up and
        you can stand a server up for them straight away — they join it by
        invitation when they are ready, rather than signing up separately.
      </p>
      <Button
        className="mt-4"
        disabled={setUp.isPending}
        onClick={() => setUp.mutate()}
        data-testid="set-client-up-on-bitswan"
      >
        {setUp.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Set {client.company_name} up on BitSwan
      </Button>
    </div>
  );
}
